import { useEffect, useRef, useState } from 'react'
import { Button } from './Button.tsx'
import { Checkbox } from './Checkbox.tsx'
import { Icon } from './Icon.tsx'
import { FOCUS_RING } from './focusRing.ts'

/**
 * A filter that takes more than one answer.
 *
 * People working a queue think in groups: "everything still mine to do" is two
 * statuses, and "still on the bench" is two stages. A single-select forces them
 * to either look twice or widen to everything — which is how a filter ends up
 * with invented options like "em andamento", a combination wearing a stage's
 * clothes because the control could only hold one value.
 *
 * **Everything selected reads as no filter, and so does nothing selected.**
 * Clearing the last tick is somebody trying to see more, not less, and an
 * empty screen reads as broken.
 *
 * The panel is a `popover`, so it escapes the table's overflow instead of
 * being clipped by it — the same reason the divergence popover is one.
 */
export interface MultiSelectOption {
  readonly value: string
  readonly label: string
  /** Drawn as a dot before the label, where the option has a colour. */
  readonly tone?: string | undefined
}

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  allLabel = 'Todos',
  namesShown = 3,
  searchable = false,
}: {
  label: string
  options: readonly MultiSelectOption[]
  selected: readonly string[]
  onChange: (next: readonly string[]) => void
  /** What the trigger says when the filter is not narrowing anything. */
  allLabel?: string
  /**
   * How many names to spell out before collapsing the rest into a count.
   *
   * "2 selecionados" makes somebody open the panel to find out which two,
   * which is the panel doing the summary's job. Naming them up to a limit
   * keeps the answer on the button, and the limit keeps the button from
   * growing until it pushes the rest of the toolbar off the row.
   */
  namesShown?: number
  /** Shows a search box in the panel once the list is long enough to need one. */
  searchable?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const anchor = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState({ left: 0, top: 0 })

  const chosen = options.filter((option) => selected.includes(option.value))
  const summary =
    chosen.length === 0 || chosen.length === options.length
      ? allLabel
      : chosen.length <= namesShown
        ? chosen.map((option) => option.label).join(', ')
        : `${chosen
            .slice(0, namesShown)
            .map((option) => option.label)
            .join(', ')} e mais ${chosen.length - namesShown}`

  const show = () => {
    const box = anchor.current?.getBoundingClientRect()
    const sheet = panel.current
    if (box === undefined || sheet === null) return
    // Shown before measuring: a popover has no size until it is in the top
    // layer, and reading its height first gives zero.
    sheet.showPopover()
    const own = sheet.getBoundingClientRect()
    setAt({
      left: Math.max(8, Math.min(box.left, window.innerWidth - own.width - 8)),
      top:
        box.bottom + own.height + 8 > window.innerHeight
          ? box.top - own.height - 4
          : box.bottom + 4,
    })
    setOpen(true)
  }

  const hide = () => {
    const sheet = panel.current
    if (sheet !== null && sheet.matches(':popover-open')) sheet.hidePopover()
    setOpen(false)
    setQuery('')
  }

  // Matched on the whole label, so a filial reads "ARTPHARMA (7)" and either
  // half finds it. Chosen options stay visible whatever was typed, so a tick
  // never disappears from under the cursor.
  const normalised = query.trim().toLowerCase()
  const shown =
    normalised === ''
      ? options
      : options.filter(
          (option) =>
            selected.includes(option.value) || option.label.toLowerCase().includes(normalised),
        )

  useEffect(() => {
    if (!open) return undefined
    const dismiss = (event: MouseEvent) => {
      const target = event.target as Node
      if (panel.current?.contains(target) === true) return
      if (anchor.current?.contains(target) === true) return
      hide()
    }
    // Deferred, or the click that opened it closes it in the same tick.
    const timer = setTimeout(() => document.addEventListener('mousedown', dismiss), 0)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', dismiss)
    }
  }, [open])

  const toggle = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((one) => one !== value) : [...selected, value],
    )
  }

  return (
    <div ref={anchor} className="relative inline-flex">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`border-stroke-weak bg-fill-white text-tSmall text-text-strong flex h-8 items-center gap-2 rounded-[var(--radius-base)] border px-3 ${FOCUS_RING}`}
        onClick={() => (open ? hide() : show())}
      >
        <span className="text-text-weak">{label}:</span>
        <span className="max-w-[260px] truncate">{summary}</span>
        <Icon name="expand" className="text-icon-neutral text-[10px]" />
      </button>

      {/*
        No `display` utility on this element, ever: a closed popover is hidden
        by a user-agent rule, and an author class beats it — which would leave
        every panel on the screen permanently open.
      */}
      <div
        ref={panel}
        popover="manual"
        role="listbox"
        aria-label={label}
        className="border-stroke-weak bg-background-raised m-0 min-w-[200px] rounded-[8px] border p-2 shadow-lg"
        style={{
          position: 'fixed',
          left: at.left,
          top: at.top,
          inset: 'auto',
        }}
      >
        <div className="flex flex-col gap-1">
          {searchable && (
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar"
              aria-label={`Buscar em ${label}`}
              autoComplete="off"
              className="border-stroke-weak text-tSmall text-text-strong mb-1 h-8 rounded-[var(--radius-base)] border px-2"
            />
          )}
          {shown.length === 0 && (
            <span className="text-tSmall text-text-weak px-2 py-1">Nada com esse nome.</span>
          )}
          {shown.map((option) => (
            <label
              key={option.value}
              className="hover:bg-fill-weak/60 flex cursor-pointer items-center gap-2 rounded-[4px] px-2 py-1"
            >
              <Checkbox
                checked={selected.includes(option.value)}
                onChange={() => toggle(option.value)}
                aria-label={option.label}
              />
              {option.tone !== undefined && (
                <span
                  aria-hidden="true"
                  className="inline-block size-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: option.tone }}
                />
              )}
              <span className="text-tSmall text-text-strong whitespace-nowrap">{option.label}</span>
            </label>
          ))}

          {/*
            One way back to everything.

            Ticking six boxes to undo a filter is worse than the filter, and
            "all" and "none" mean the same thing here — so clearing is the
            honest verb for both.
          */}
          <Button
            size="small"
            variant="tertiary"
            tone="neutral"
            className="mt-1 self-start"
            onClick={() => onChange([])}
          >
            Limpar
          </Button>
        </div>
      </div>
    </div>
  )
}
